import React from 'react';
import useAppForm from '@app/site/_form';
import { useSelectedCompanyHotel } from '@app/site/secured/rateshopper/rates/provider/useSelectedCompanyHotel';
import * as z from 'zod/v4';
import { SubmitButton } from '@app/site/_form/button/SubmitButton';
import { ResetButton } from '@app/site/_form/button/ResetButton';
import {
	useSaveCompanyHotelAvailabilitySettingsMutation,
} from '@app/model/company_hotel/service/CompanyHotelSettingsServiceHooks';
import useToaster from '@app/component/overlays/toaster/ToasterHook';


const RateshopperAvailabilitySettingsPage = React.memo(() => {

	const { companyHotel } = useSelectedCompanyHotel();
	const toaster = useToaster();

	const RangeValidation = z.object({
		from: z
			.number({ message: 'From must be a number' })
			.min(0, 'From must be between 0 and 100'),
		to: z
			.number({ message: 'To must be a number' })
			.min(0, 'To must be between 0 and 100'),
	}).superRefine((range, ctx) => {
		if (range.from >= range.to) {
			ctx.addIssue({
				code: 'custom',
				path: ['to'],
				message: 'To must be greater than From',
			});
		}
	});

	const FormValidation = z.object({
		availabilityWarningRange: RangeValidation,
	});

	const saveAvailabilitySettingsMutation = useSaveCompanyHotelAvailabilitySettingsMutation({
		onSuccess: () => {
			toaster.success('Settings', 'Availability settings saved successfully');
		},
	});

	const form = useAppForm({
		defaultValues: {
			availabilityWarningRange: companyHotel.settings.calendar.availabilityWarningRange,
		},
		validators: {
			onChange: FormValidation,
		},
		onSubmit: ({ value }) => saveAvailabilitySettingsMutation.mutateAsync({
			companyHotelId: companyHotel.id,
			input: value,
		}).catch((e: string) => toaster.error('Settings', e)),

	});

	return <form.AppForm>
		<form onSubmit={(e) => {
			e.preventDefault();
			e.stopPropagation();
		}}>
			<div className="bg-white p-4 sm:p-10 rounded-xl">
				<div><h2 className="text-base font-semibold leading-5 text-gray-900">Price Method</h2>
					<p className="text-sm leading-6 text-gray-500">
						Customize the percentage difference below to trigger alerts as desired.
					</p>

					<fieldset className="mt-3 border-t border-gray-200">
						<div className="relative mb-7 mt-4">
							<dt className="text-sm font-semibold leading-7 text-gray-900">
								How should I set the availability setting?
							</dt>
							<dd className="mt-0.5 text-sm text-gray-600">
								Below, you can establish two warning levels for the number of your available rooms.
								The first level indicates a warning zone for low availability, while the second
								denotes a warning zone for high availability.
							</dd>
						</div>

						<div className="mb-6">
							<p className="text-sm font-semibold leading-7 text-gray-900">Availability warning levels</p>
							<div className="grid grid-cols-1 gap-4 sm:grid-cols-3 sm:max-w-3xl">
								<div
									className="inline-flex items-center justify-center rounded-md px-1 py-1.5 text-sm font-medium ring-1 ring-inset ring-amber-700/10 bg-amber-50 text-amber-500">High
								</div>
								<div
									className="inline-flex items-center justify-center rounded-md bg-slate-50 px-1 py-1.5 text-sm font-medium ring-1 ring-inset ring-slate-700/10 text-slate-500">Moderate
								</div>
								<div
									className="inline-flex items-center justify-center rounded-md px-1 py-1.5 text-sm font-medium ring-1 ring-inset ring-green-700/10 bg-green-50 text-green-500">Low
								</div>
							</div>
						</div>

						<div><p className="text-sm font-semibold leading-7 text-gray-900 mb-0.5">Adjust availability warning
							levels</p>
							<div className="grid grid-cols-6 gap-y-3 gap-x-3 sm:max-w-3xl">
								<div className="col-span-2"><input
									type="text"
									className="input input-disabled"
									disabled
									value="less than"
								/></div>
								<div className="col-span-2">
									<form.AppField name="availabilityWarningRange.from">
										{(field) => {
											return <field.NumberField min={1} showControls step={5} />;
										}}
									</form.AppField>
								</div>
								<div className="col-span-2">
									<div className="grid grid-cols-1"><input
										type="text"
										className="input input-disabled col-start-1 row-start-1 block w-full rounded-md bg-white py-1.5 pr-3 pl-7 text-gray-900 outline-1 -outline-offset-1 outline-gray-300"
										disabled
										value="Low"
									/><span
										className="pointer-events-none col-start-1 row-start-1 ml-3 self-center inline-block w-2 h-2 shrink-0 rounded-full border border-transparent bg-green-500"></span>
									</div>
								</div>
								<div className="col-span-2"><input
									type="text"
									className="input input-disabled"
									disabled
									value="more than"
								/></div>
								<div className="col-span-2">
									<form.AppField name="availabilityWarningRange.to">
										{(field) => {
											return <field.NumberField min={1} showControls step={5} />;
										}}
									</form.AppField>
								</div>
								<div className="col-span-2">
									<div className="grid grid-cols-1"><input
										type="text"
										className="input input-disabled col-start-1 row-start-1 block w-full rounded-md bg-white py-1.5 pr-3 pl-7 text-gray-900 outline-1 -outline-offset-1 outline-gray-300"
										disabled
										value="High"
									/><span
										className="pointer-events-none col-start-1 row-start-1 ml-3 self-center inline-block w-2 h-2 shrink-0 rounded-full border border-transparent bg-amber-500"></span>
									</div>
								</div>
							</div>
						</div>
					</fieldset>
					<div className="flex items-center justify-end gap-x-4 border-t border-gray-200 pt-4 mt-6">
						<ResetButton>Reset</ResetButton>
						<SubmitButton state="primary">Save</SubmitButton>
					</div>
				</div>
			</div>
		</form>
	</form.AppForm>;
});

export default RateshopperAvailabilitySettingsPage;
